import { Arg, Metadata } from '../../../utils/spds/metadata';
import {
  MODAL_DOCS_FULL_SCREEN_BLOCKS,
  assembleModalDescription,
} from '../../../utils/spds/metadata/modal-docs';
import { hash } from '@ember/helper';
import { registerDestructor } from '@ember/destroyable';
import Body from './full-screen/body.gjs';
import FlexColumn from '../layout/flex/column.gjs';
import FlexRow from '../layout/flex/row.gjs';
import Footer from './full-screen/footer.gjs';
import Header from './full-screen/header.gjs';
import Preview from './full-screen/preview.gjs';
import Super from '../core/modal.gjs';
import styles from './full-screen.module.scss';

const METADATA = Metadata.define({
  description: assembleModalDescription(
    'Full-screen modal for multi-step flows; interrupts workflow and focuses attention.',
    MODAL_DOCS_FULL_SCREEN_BLOCKS
  ),
  meta: {
    currentStep: Arg.number({
      description: 'Current step index for multi-step flows',
      initialValue: 1,
    }),
    stepCount: Arg.number({
      description: 'Total number of steps for multi-step flows',
      initialValue: 4,
    }),
  },
  name: 'Full Screen',
});

export default class SpdsModalFullScreen extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  baseClass = 'spds-modal-full-screen';
  modal = true;
  styles = styles;
  elementsToRegister = [
    { name: 'mainModalBody', selector: '.spds-modal-full-screen-main-body' },
    { name: 'modalBody', selector: '.spds-modal-full-screen-body' },
  ];

  get bodyId() {
    return this.composeSubcomponentId('body');
  }

  get contentId() {
    return this.composeSubcomponentId('content');
  }

  get footerId() {
    return this.composeSubcomponentId('footer');
  }

  get headerId() {
    return this.composeSubcomponentId('header');
  }

  get mainBodyId() {
    return this.composeSubcomponentId('main-body');
  }

  get previewId() {
    return this.composeSubcomponentId('preview');
  }

  setup() {
    super.setup();

    if (!(this.mainModalBody && this.modalBody)) return;

    let scrollHandler = ({ deltaY }) => {
      this.modalBody.scrollTop += deltaY;
    };
    let options = { passive: true };

    this.mainModalBody.addEventListener('wheel', scrollHandler, options);

    registerDestructor(this, () => {
      this.mainModalBody.removeEventListener('wheel', scrollHandler, options);
    });
  }

  <template>
    <FlexColumn
      @fixed={{true}}
      @id={{this.id}}
      @tag="dialog"
      class={{this.classes}}
      data-test-spds-modal-full-screen
      ...attributes
      {{this.setupComponent}}
      {{this.validateArguments}}
    >
      <FlexColumn class="inner spds-modal-full-screen-inner" data-test-spds-modal-full-screen-inner>
        {{yield
          (hash
            Header=(component
              Header
              close=this.beforeClose
              currentStep=@currentStep
              id=this.headerId
              stepCount=@stepCount
            )
            beforeClose=this.beforeClose
            close=this.close
          )
        }}
        <FlexRow
          @id={{this.contentId}}
          class="content spds-modal-full-screen-content"
          data-test-spds-modal-full-screen-content
        >
          <FlexColumn
            @id={{this.mainBodyId}}
            class="main-body spds-modal-full-screen-main-body"
            data-test-spds-modal-full-screen-main-body
          >
            {{yield
              (hash
                Body=(component Body id=this.bodyId)
                Footer=(component Footer currentStep=@currentStep id=this.footerId)
                beforeClose=this.beforeClose
                close=this.close
              )
            }}
          </FlexColumn>
          {{yield (hash Preview=(component Preview id=this.previewId))}}
        </FlexRow>
      </FlexColumn>
    </FlexColumn>
  </template>
}

export const MODAL_FULL_SCREEN_METADATA = SpdsModalFullScreen.metadata;
